import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lightning, Plus, Trash, X } from "@phosphor-icons/react";
import { getDrivers, addDrivers, deleteDriver, clearDrivers } from "@/lib/procureApi";
import { fmtInt } from "@/lib/format";

const DriverChip = ({ code, canEdit, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-sm bg-[#FDE2E4] px-2 py-0.5 font-mono text-[11px] font-bold text-[#B91C1C]" data-testid={`driver-chip-${code}`}>
    {code}
    {canEdit && (
      <button data-testid={`driver-remove-${code}`} onClick={() => onRemove(code)} className="hover:text-black" title="Remove">
        <X size={10} weight="bold" />
      </button>
    )}
  </span>
);

export const ProductionDrivers = ({ isAdmin, onChanged }) => {
  const [codes, setCodes] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { setCodes(((await getDrivers()).drivers || []).map((d) => d.code)); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const run = async (fn, msg) => {
    setBusy(true);
    try { const r = await fn(); toast.success(msg(r)); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update Production Drivers"); }
    finally { setBusy(false); }
  };

  const add = () => text.trim() && run(() => addDrivers(text), (r) => {
    setText("");
    return `Added ${fmtInt(r.added)} item code(s)${r.skipped ? ` · ${r.skipped} already listed` : ""}`;
  });
  const remove = (c) => run(() => deleteDriver(c), () => `Removed ${c}`);
  const clear = () => window.confirm("Remove ALL Production Driver item codes?") && run(clearDrivers, () => "Production Drivers cleared");

  return (
    <div className="space-y-2" data-testid="production-drivers">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 pf-label"><Lightning size={13} weight="bold" />Production Drivers</div>
        <span className="font-mono text-[11px] text-[#64748B]" data-testid="drivers-count">{fmtInt(codes.length)} item codes</span>
      </div>
      <p className="text-xs text-[#64748B]">OR / OQ / OF lines with these item codes are marked <b className="rounded-sm bg-[#FDE2E4] px-1 text-[#B91C1C]">bold red</b> as critical.</p>
      {isAdmin && (
        <div className="space-y-2 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] p-3">
          <textarea
            data-testid="drivers-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={4}
            placeholder="Paste item codes — one per line, or separated by commas / tabs"
            className="w-full rounded-sm border border-[#E3E7EC] bg-white p-2 font-mono text-xs text-[#0F172A] outline-none focus:border-[#F0323E]"
          />
          <div className="flex gap-2">
            <button data-testid="drivers-add" onClick={add} disabled={busy || !text.trim()} className="flex flex-1 items-center justify-center gap-1.5 rounded-sm bg-[#F0323E] px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#d92834] disabled:opacity-50">
              <Plus size={14} weight="bold" /> Add item codes
            </button>
            <button data-testid="drivers-clear" onClick={clear} disabled={busy || !codes.length} title="Remove all" className="flex items-center justify-center rounded-sm border border-[#E3E7EC] px-3 text-[#64748B] transition-colors hover:bg-[#FEE2E2] hover:text-[#F0323E] disabled:opacity-40">
              <Trash size={14} weight="bold" />
            </button>
          </div>
        </div>
      )}
      {codes.length > 0 && (
        <div className="flex max-h-40 flex-wrap gap-1 overflow-y-auto rounded-sm border border-[#E3E7EC] p-2" data-testid="drivers-list">
          {codes.map((c) => <DriverChip key={c} code={c} canEdit={isAdmin} onRemove={remove} />)}
        </div>
      )}
    </div>
  );
};
